import { Copy, Loader2, Mail, RefreshCw, UserPlus, XCircle } from "lucide-react";
import { useState } from "react";

import { adminService } from "@/api/admin/admin.service";
import { extractUsersList } from "@/api/admin/admin.types";
import type { TInvestorInvite, TInviteStatus } from "@/api/investorVetting";
import { investorVettingService } from "@/api/investorVetting";
import { DataGrid, type TColumnDef, type TDataGridQuery, type TFilterDef } from "@/components/data-grid";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { formatLocaleDate } from "@/utils/date";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: TInviteStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "expired", label: "Expired" },
  { value: "registered", label: "Registered" },
  { value: "revoked", label: "Revoked" },
];

function InviteStatusBadge({ status }: { status: TInviteStatus }) {
  const styles: Record<TInviteStatus, string> = {
    pending: "border-primary/30 bg-primary/10 text-primary",
    expired: "border-destructive/30 bg-destructive/10 text-destructive",
    registered:
      "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400",
    revoked: "border-border bg-muted text-muted-foreground",
  };
  return <Badge className={styles[status]}>{status}</Badge>;
}

type TInviteForm = {
  first_name: string;
  last_name: string;
  email: string;
};

const EMPTY_FORM: TInviteForm = { first_name: "", last_name: "", email: "" };

export function InvestorInvitationsPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState<TInviteForm>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // RD-17: when the email belongs to an existing user, offer the grant instead.
  const [grantCandidate, setGrantCandidate] = useState<{ userId: string; email: string } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<TInvestorInvite | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  const fetcher = async (query: TDataGridQuery) => {
    const status = query.filters.status;
    const { data, error } = await investorVettingService.getInvites(
      {
        limit: query.limit,
        offset: query.offset,
        ...(typeof status === "string" && status ? { status } : {}),
      },
      { signal: query.signal }
    );
    if (error) throw error;
    return data;
  };

  const isFormValid =
    form.first_name.trim().length > 0 && form.last_name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.email);

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setForm(EMPTY_FORM);
    setGrantCandidate(null);
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    const { data, error } = await investorVettingService.createInvite({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim().toLowerCase(),
    });
    if (error) {
      const message = await extractErrorMessage(error);
      if (message.toLowerCase().includes("grant investor access")) {
        // Existing user: look them up and offer the RD-17 grant path.
        const search = await adminService.getUsers({ search: form.email.trim().toLowerCase(), page_size: 5 });
        const match = extractUsersList(search.data?.data).find(
          (candidate) => candidate.email.toLowerCase() === form.email.trim().toLowerCase()
        );
        if (match) {
          setGrantCandidate({ userId: match.id, email: match.email });
        } else {
          toast({ variant: "destructive", title: "User already exists", description: message });
        }
      } else {
        toast({ variant: "destructive", title: "Failed to create invitation", description: message });
      }
    } else if (data) {
      toast({ title: "Invitation sent", description: `${form.email.trim()} has been invited as an investor.` });
      closeInviteDialog();
      reload();
    }
    setIsSubmitting(false);
  };

  const handleGrant = async () => {
    if (!grantCandidate) return;
    setIsSubmitting(true);
    const { error } = await investorVettingService.grantInvestorAccess(grantCandidate.userId);
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to grant investor access",
        description: await extractErrorMessage(error),
      });
    } else {
      toast({ title: "Investor access granted", description: `${grantCandidate.email} can now act as an investor.` });
      closeInviteDialog();
      reload();
    }
    setIsSubmitting(false);
  };

  const handleResend = async (invite: TInvestorInvite) => {
    const { error } = await investorVettingService.resendInvite(invite.id);
    if (error) {
      toast({ variant: "destructive", title: "Failed to resend", description: await extractErrorMessage(error) });
    } else {
      toast({ title: "Invitation re-sent", description: "The same link was emailed again (expiry unchanged)." });
    }
  };

  const handleRecreate = async (invite: TInvestorInvite) => {
    const { error } = await investorVettingService.recreateInvite(invite.id);
    if (error) {
      toast({ variant: "destructive", title: "Failed to recreate", description: await extractErrorMessage(error) });
    } else {
      toast({ title: "Invitation recreated", description: "A fresh link with a new expiry window was emailed." });
      reload();
    }
  };

  const handleCopyLink = async (invite: TInvestorInvite) => {
    if (!invite.invite_url) return;
    await navigator.clipboard.writeText(invite.invite_url);
    toast({ title: "Invite link copied" });
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    const { error } = await investorVettingService.revokeInvite(revokeTarget.id);
    if (error) {
      toast({ variant: "destructive", title: "Failed to revoke", description: await extractErrorMessage(error) });
    } else {
      toast({ title: "Invitation revoked" });
      reload();
    }
    setIsRevoking(false);
    setRevokeTarget(null);
  };

  const columns: TColumnDef<TInvestorInvite>[] = [
    {
      id: "invitee",
      header: "Invitee",
      type: "user",
      accessor: (invite) => ({ name: `${invite.first_name} ${invite.last_name}`, email: invite.email }),
    },
    {
      id: "status",
      header: "Status",
      accessor: (invite) => invite.status,
      cell: (invite) => <InviteStatusBadge status={invite.status} />,
    },
    {
      id: "created_at",
      header: "Invited",
      type: "relativeTime",
      accessor: (invite) => invite.created_at,
    },
    {
      id: "expires_at",
      header: "Expires",
      accessor: (invite) => invite.expires_at,
      cell: (invite) => <span className="text-muted-foreground">{formatLocaleDate(new Date(invite.expires_at))}</span>,
    },
  ];

  const filters: TFilterDef[] = [{ id: "status", type: "select", label: "Status", options: STATUS_OPTIONS }];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Investor Invitations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Invite trusted investors — they skip email verification and Sales vetting.
            </p>
          </div>
        </div>

        <DataGrid<TInvestorInvite>
          getRowId={(row) => row.id}
          persistKey="admin.investorInvites"
          ariaLabel="Investor invitations"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50] }}
          toolbar={{
            refresh: true,
            columnVisibility: true,
            density: true,
            create: {
              label: "Invite Investor",
              icon: <UserPlus className="h-4 w-4" />,
              onClick: () => setInviteOpen(true),
            },
          }}
          rowActions={(invite) =>
            invite.status === "pending"
              ? [
                  {
                    label: "Copy link",
                    icon: <Copy className="h-4 w-4" />,
                    onClick: () => void handleCopyLink(invite),
                  },
                  { label: "Resend", icon: <Mail className="h-4 w-4" />, onClick: () => void handleResend(invite) },
                  {
                    label: "Revoke",
                    icon: <XCircle className="h-4 w-4" />,
                    variant: "destructive",
                    onClick: () => setRevokeTarget(invite),
                  },
                ]
              : invite.status === "expired" || invite.status === "revoked"
                ? [
                    {
                      label: "Recreate",
                      icon: <RefreshCw className="h-4 w-4" />,
                      onClick: () => void handleRecreate(invite),
                    },
                  ]
                : []
          }
          emptyState={{ title: "No invitations yet.", description: "Invite a trusted investor to get started." }}
        />

        <Dialog
          open={inviteOpen}
          onOpenChange={isSubmitting ? undefined : (open) => (open ? setInviteOpen(true) : closeInviteDialog())}
        >
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader>
              <DialogTitle>Invite an investor</DialogTitle>
              <DialogDescription>
                They receive an email with a registration link that pre-fills their details and skips email
                verification.
              </DialogDescription>
            </DialogHeader>

            {grantCandidate ? (
              <div className="rounded-md border border-border bg-muted/50 p-3 text-sm">
                <p className="font-medium text-foreground">{grantCandidate.email} already has an account.</p>
                <p className="mt-1 text-muted-foreground">
                  You can grant them investor access directly instead — no registration email needed.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Input
                  placeholder="First name"
                  value={form.first_name}
                  onChange={(event) => setForm((f) => ({ ...f, first_name: event.target.value }))}
                />
                <Input
                  placeholder="Last name"
                  value={form.last_name}
                  onChange={(event) => setForm((f) => ({ ...f, last_name: event.target.value }))}
                />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(event) => setForm((f) => ({ ...f, email: event.target.value }))}
                />
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={closeInviteDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {grantCandidate ? (
                <Button
                  onClick={() => void handleGrant()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="mr-2 h-4 w-4" />
                  )}
                  Grant investor access
                </Button>
              ) : (
                <Button
                  onClick={() => void handleCreate()}
                  disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Send invitation
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={revokeTarget !== null}
          onOpenChange={(open) => !open && setRevokeTarget(null)}
          title="Revoke this invitation?"
          description={
            revokeTarget
              ? `${revokeTarget.email} will no longer be able to register with this link. You can recreate the invitation later.`
              : undefined
          }
          confirmLabel="Revoke"
          isConfirming={isRevoking}
          onConfirm={handleRevoke}
        />
      </div>
    </div>
  );
}
